import { CxCard, CxTabButton, CxWrap, Line } from '../../../components/Sx';
import FormInputField from '../../../../common/components/formElements/FormInputField';
import FormTextArea from '../../../../common/components/formElements/FormTextArea';

import { z } from 'zod';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import ControlledFormSelectDrop from '../../../../common/components/formElements/controlled/ControlledFormSelectDrop';
import { useEffect, useMemo, useState } from 'react';
import { City, Own, SecondarySpecialty } from '../../../services/DoctorProfile';
import CustomSuccessModal from '../../../../common/components/customSuccessModal/CustomSuccessModal';
import FailNotification from '../../../../common/components/failNotification/FailNotification';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { useInfoStore } from '../../../../App';

const latRegExp = /^[+-]?(?:([0-8]\d{0,1}(\.\d+){0,})|(90))?$/;
const longRegExp = /^[+-]?(?:([1]{1}[0-7]\d{0,1}(\.\d+){0,})|([0]{0,1}[0-9]\d{0,1}(\.\d+){0,})|(180))?$/;
const costRegExp = /^(\d+|\d+-\d+)$/;
const costRegExpFlexible = /^[a-zA-Z0-9\s\-–—.,$€£()]+$/;

const formSchema = z.object({
    patientContactPhone1: z.string({ required_error: 'Requerido' }).nonempty({ message: 'Requerido' }),
    patientContactPhone1CountryCode: z.string(),
    patientContactPhone2: z.string(),
    patientContactPhone2CountryCode: z.string(),
    consultationCost: z
        .string({ required_error: 'Requerido' })
        .nonempty({ message: 'Requerido' })
        .regex(costRegExpFlexible, {
            message: 'Formato inválido. Usa números, letras, rangos (50-100), o texto como "Gratis", "50 USD", etc.'
        }),
    examCost: z
        .string()
        .regex(costRegExpFlexible, {
            message: 'Formato inválido. Usa números, letras, rangos (50-100), o texto como "Gratis", "50 USD", etc.'
        })
        .optional()
        .or(z.literal('')),
    cash: z.boolean(),
    card: z.boolean(),
    transfers: z.boolean(),
    remesa: z.boolean(),
    bitcoin: z.boolean(),
    governmentPermitInfo: z.string(),
    generalInfo: z.string(),
    insurance: z.string(),
    // addresses: z.string(),
    lat_long: z.string(),
    idCity: z.string({ required_error: 'Requerido' }).refine((value) => {
        return value.trim().length > 0;
    }, 'Requerido'),
    latitude: z
        .string()
        .regex(latRegExp, { message: 'Número no válido, la latitud debe ser un número entre -90 y 90' }),
    longitude: z
        .string()
        .regex(longRegExp, { message: 'Número no válido, la longitud debe ser un número entre -180 y 180' })
});

const OwnProfile = () => {
    const [address, setAddress] = useState([{ address: '' }]);
    const [addressError, setAddressError] = useState(['']);
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [showErrorModal, setShowErrorModal] = useState(false);
    const [successBody, setSuccessBody] = useState('');
    const [errorMsg, setErrorBody] = useState('');
    const [allCities, setAllCities] = useState([]);
    const { userInfo } = useInfoStore((state) => state);

    const { register, handleSubmit, formState, getValues, reset, watch, setValue, control } = useForm({
        resolver: zodResolver(formSchema)
    });
    const { errors } = formState;

    const watchedCity = watch('idCity');

    const closeError = () => {
        setShowErrorModal(false);
    };
    const addDisease = () => {
        if (address.length < 3) {
            setAddress([...address, { address: '' }]);
            setAddressError([...addressError, '']);
        }
    };

    const removeDisease = (index) => {
        const newAddress = [...address];
        const newAddressError = [...addressError];
        newAddress.splice(index, 1);
        newAddressError.splice(index, 1);
        setAddress(newAddress);
        setAddressError(newAddressError);
    };
    const validateDiseases = () => {
        const hasEmpty = address?.some((d) => d?.address?.trim() === '');
        return !hasEmpty;
    };
    const saveForm = (values) => {
        const {
            latitude,
            longitude,
            idCity,
            card,
            cash,
            bitcoin,
            remesa,
            transfers,
            patientContactPhone1,
            patientContactPhone1CountryCode,
            patientContactPhone2,
            patientContactPhone2CountryCode
        } = values;
        console.log(values);
        const paymentMethodsArray = [];
        if (remesa) paymentMethodsArray.push('Remesa');
        if (transfers) paymentMethodsArray.push('Transferencias');
        if (bitcoin) paymentMethodsArray.push('Bitcoin');
        if (card) paymentMethodsArray.push('Tarjeta de Crédito');
        if (cash) paymentMethodsArray.push('Efectivo');
        if (!validateDiseases()) {
            setAddressError(address.map(() => 'Este campo no puede estar vacío'));
            setShowErrorModal(true);
            return;
        }

        const paymentMethods = paymentMethodsArray.join(', ') || false;

        // Remove country code from phone numbers for payload
        const cleanPhone1 = patientContactPhone1?.replace(patientContactPhone1CountryCode || '', '') || '';
        const cleanPhone2 = patientContactPhone2?.replace(patientContactPhone2CountryCode || '', '') || '';

        Own.updateProfile({
            ...values,
            patientContactPhone1: cleanPhone1,
            patientContactPhone1CountryCode,
            patientContactPhone2: cleanPhone2,
            patientContactPhone2CountryCode,
            latitude,
            longitude,
            idCity: Number(idCity),
            paymentMethods,
            addresses: address
        })
            .then(() => _createSuccessMessage('Success'))
            .catch((err) => {
                setShowErrorModal(true);
                setErrorBody(err.response.data.message);
            });
    };

    const _createSuccessMessage = (message) => {
        setShowSuccessModal(true);
        setSuccessBody(message);
    };

    const onCloseSuccessMessage = () => {
        setShowSuccessModal(false);
        setSuccessBody('');
    };

    const handleDiseaseChange = (e, index) => {
        const { value } = e.target;
        const newAddress = [...address];
        const newAddressError = [...addressError];

        newAddress[index].address = value;
        newAddressError[index] = value.trim() === '' ? 'Este campo no puede estar vacío' : '';

        setAddress(newAddress);
        setAddressError(newAddressError);
    };

    // const allSecSpecialties = async () => {
    //   const getAllSpecialty = await SecondarySpecialty.getAllSecondarySpecialty();
    //   if (getAllSpecialty) {
    //     const allFormattedsecSpecialties = getAllSpecialty.data.map((specialty) => ({
    //       id: specialty.id,
    //       label: specialty.name,
    //       key: specialty.idLanguageByDoctor
    //     }));
    //     // console.log({ allFormattedsecSpecialties });
    //     setAllSecSpecialties(allFormattedsecSpecialties);
    //   }
    // };

    const queryAllCities = async () => {
        const getAllLanguages = await City.getCityList();
        if (getAllLanguages) {
            const formattedAllLanguages = getAllLanguages.data.map((city) => ({ id: city.id, label: city.name }));
            // console.log(formattedAllLanguages);
            setAllCities(formattedAllLanguages);
        }
    };

    useEffect(() => {
        // allSecSpecialties();
        Own.getProfile()
            .then((res) => {
                const fetchedAddresses = res.data.addresses ?? [];

                reset({
                    patientContactPhone1: res.data.patientContactPhone1 ?? '',
                    patientContactPhone1CountryCode: res.data.patientContactPhone1CountryCode ?? '+503',
                    patientContactPhone2: res.data.patientContactPhone2 ?? '',
                    patientContactPhone2CountryCode: res.data.patientContactPhone2CountryCode ?? '+503',
                    consultationCost: res.data.consultationCost ?? '',
                    examCost: res.data.examCost ?? '',
                    governmentPermitInfo: res.data.governmentPermitInfo ?? '',
                    generalInfo: res.data.generalInfo ?? '',
                    insurance: res.data.insurance ?? '',
                    // address: res.data.addresses ?? '',
                    idCity: res.data.idCity?.toString() ?? '',
                    cash: res.data.paymentMethods?.includes('Efectivo') ?? false,
                    bitcoin: res.data.paymentMethods?.includes('Bitcoin') ?? false,
                    remesa: res.data.paymentMethods?.includes('Remesa') ?? false,
                    transfers: res.data.paymentMethods?.includes('Transferencias') ?? false,
                    card: res.data.paymentMethods?.includes('Tarjeta de Crédito') ?? false,
                    latitude: res.data.latitude ?? '',
                    longitude: res.data.longitude ?? '',
                    addresses: res.data.addresses ?? '',
                    lat_long: res.data.latitude ? res.data.latitude + res.data.longitude : ''
                });
                setAddress(
                    fetchedAddresses.length > 0
                        ? fetchedAddresses.map((addr) => ({ address: addr.address }))
                        : [{ address: '' }]
                );
            })
            .catch((err) => console.log(err));
        queryAllCities();
        // console.clear();
    }, []);

    // console.log(
    //   'game',
    //   allSecSpecialty.find((item) => item.id === getValues().idSpecialty)
    // );

    return (
        <form onSubmit={handleSubmit(saveForm)}>
            <CxWrap>
                <CxCard>Contacto</CxCard>
                <Line />
                <CxCard>
                    <div className="grid grid-cols-12 gap-[30px]">
                        <div className="form-label-box col-span-12 md:col-span-6">
                            <label className="block text-sm font-medium text-gray-700 mb-2">Teléfono Principal*</label>
                            <Controller
                                name="patientContactPhone1"
                                control={control}
                                render={({ field: { onChange, value, ref } }) => {
                                    const countryCode = getValues('patientContactPhone1CountryCode') || '+503';
                                    const phoneNumber = value || '';
                                    const mergedValue = countryCode + phoneNumber;

                                    return (
                                        <PhoneInput
                                            value={mergedValue}
                                            country="sv"
                                            onChange={(val, country) => {
                                                const codeVal = `+${country.dialCode}`;
                                                const phoneVal = val.replace(country.dialCode, '').replace(/^\+/, '');

                                                setValue('patientContactPhone1CountryCode', codeVal);
                                                setValue('patientContactPhone1', phoneVal);

                                                onChange(phoneVal);
                                            }}
                                            enableSearch
                                            disableDropdown={false}
                                            disableCountryCode={false}
                                            disableAreaCodes={true}
                                            countryCodeEditable={false}
                                            placeholder="Ingrese su número"
                                            containerClass="rounded-full w-full custom-phone-container1"
                                            inputClass="w-full h-10 pl-14 pr-4 rounded-full border border-gray-300 focus:outline-none focus:ring-0 focus:border-gray-300 active:outline-none active:ring-0 active:border-gray-300"
                                            buttonClass="rounded-l-full border-r border-gray-300 bg-white focus:outline-none focus:ring-0 focus:border-gray-300 active:outline-none active:ring-0 active:border-gray-300"
                                            specialLabel=""
                                            inputProps={{ ref }}
                                        />
                                    );
                                }}
                            />
                            {errors.patientContactPhone1 && (
                                <span className="mt-2 text-sm text-red-700">
                                    {errors.patientContactPhone1?.message}
                                </span>
                            )}
                        </div>
                        <div className="col-span-12 md:col-span-6">
                            <label className="block text-sm font-medium text-gray-700 mb-2">Teléfono Secundario</label>
                            <Controller
                                name="patientContactPhone2"
                                control={control}
                                render={({ field: { onChange, value, ref } }) => {
                                    const countryCode = getValues('patientContactPhone2CountryCode') || '+503';
                                    const phoneNumber = value || '';
                                    const mergedValue = countryCode + phoneNumber;

                                    return (
                                        <PhoneInput
                                            value={mergedValue}
                                            country="sv"
                                            onChange={(val, country) => {
                                                const codeVal = `+${country.dialCode}`;
                                                const phoneVal = val.replace(country.dialCode, '').replace(/^\+/, '');

                                                setValue('patientContactPhone2CountryCode', codeVal);
                                                setValue('patientContactPhone2', phoneVal);

                                                onChange(phoneVal);
                                            }}
                                            enableSearch
                                            disableDropdown={false}
                                            disableCountryCode={false}
                                            disableAreaCodes={true}
                                            countryCodeEditable={false}
                                            placeholder="Ingrese su número"
                                            containerClass="rounded-full w-full custom-phone-container1"
                                            inputClass="w-full h-10 pl-14 pr-4 rounded-full border border-gray-300 focus:outline-none focus:ring-0 focus:border-gray-300 active:outline-none active:ring-0 active:border-gray-300"
                                            buttonClass="rounded-l-full border-r border-gray-300 bg-white focus:outline-none focus:ring-0 focus:border-gray-300 active:outline-none active:ring-0 active:border-gray-300"
                                            specialLabel=""
                                            inputProps={{ ref }}
                                        />
                                    );
                                }}
                            />
                            {errors.patientContactPhone2 && (
                                <span className="mt-2 text-sm text-red-700">
                                    {errors.patientContactPhone2?.message}
                                </span>
                            )}
                        </div>
                    </div>
                </CxCard>
            </CxWrap>

            <CxWrap>
                <CxCard>Costos</CxCard>
                <Line />
                <CxCard>
                    <div className="grid grid-cols-12 gap-[30px]">
                        <div className="form-label-box col-span-12 md:col-span-6">
                            <FormInputField
                                identifier="consultationCost"
                                label="*Costo de consulta"
                                helperText={'Requerido'}
                                type="string"
                                registration={register('consultationCost')}
                                errorMessage={errors.consultationCost?.message}
                            />
                        </div>

                        <div className="col-span-12 md:col-span-6">
                            <FormInputField
                                identifier="examCost"
                                label="Costo de examen"
                                type="string"
                                registration={register('examCost')}
                                errorMessage={errors.examCost?.message}
                            />
                        </div>
                    </div>
                    <div className="px-4">
                        <div className="mb-4">Métodos de pago </div>
                        <div className="flex items-center mb-4">
                            <input
                                id="cash"
                                type="checkbox"
                                name="cash"
                                className="h-4 w-4 border-gray-300 focus:ring-2 focus:ring-blue-300 rounded-full"
                                {...register('cash')}
                            />
                            <label htmlFor="cash" className="text-sm font-medium text-gray-900 ml-2 block">
                                Efectivo
                            </label>
                        </div>
                        <div className="flex items-center mb-4">
                            <input
                                id="card"
                                type="checkbox"
                                name="card"
                                className="h-4 w-4 border-gray-300 focus:ring-2 focus:ring-blue-300 rounded-full"
                                {...register('card')}
                            />
                            <label htmlFor="card" className="text-sm font-medium text-gray-900 ml-2 block">
                                Tarjeta de Crédito
                            </label>
                        </div>
                        <div className="flex items-center mb-4">
                            <input
                                id="transfers"
                                type="checkbox"
                                name="transfers"
                                className="h-4 w-4 border-gray-300 focus:ring-2 focus:ring-blue-300 rounded-full"
                                {...register('transfers')}
                            />
                            <label htmlFor="transfers" className="text-sm font-medium text-gray-900 ml-2 block">
                                Transferencias
                            </label>
                        </div>
                        <div className="flex items-center mb-4">
                            <input
                                id="bitcoin"
                                type="checkbox"
                                name="bitcoin"
                                className="h-4 w-4 border-gray-300 focus:ring-2 focus:ring-blue-300 rounded-full"
                                {...register('bitcoin')}
                            />
                            <label htmlFor="bitcoin" className="text-sm font-medium text-gray-900 ml-2 block">
                                Bitcoin
                            </label>
                        </div>
                        <div className="flex items-center mb-4">
                            <input
                                id="remesa"
                                type="checkbox"
                                name="remesa"
                                className="h-4 w-4 border-gray-300 focus:ring-2 focus:ring-blue-300 rounded-full"
                                {...register('remesa')}
                            />
                            <label htmlFor="remesa" className="text-sm font-medium text-gray-900 ml-2 block">
                                Remesa
                            </label>
                        </div>
                    </div>
                </CxCard>
            </CxWrap>

            <CxWrap>
                <CxCard>Permiso para ejercer</CxCard>
                <Line />
                <CxCard>
                    <div className="grid grid-cols-6 gap-4  pb-8 px-4">
                        <div className="col-start-1 col-end-7 form-label-box">
                            <FormInputField
                                identifier="governmentPermitInfo"
                                label="Permiso para ejercer"
                                type="string"
                                registration={register('governmentPermitInfo')}
                            />
                        </div>
                    </div>
                </CxCard>
            </CxWrap>
            <CxWrap>
                <CxCard>Sobre el consultorio</CxCard>
                <Line />
                <CxCard>
                    <div className="grid grid-cols-6 gap-4  pb-8 px-4">
                        <div className="col-start-1 col-end-12 form-label-box">
                            <FormTextArea
                                identifier="generalInfo"
                                label="Informacion general de consulta"
                                type="string"
                                registration={register('generalInfo')}
                                errorMessage={errors.generalInfo?.message}
                            />
                        </div>
                        <div className="col-start-1 col-end-12 form-label-box">
                            <FormInputField
                                identifier="insurance"
                                label="Aseguradoras con las que trabaja"
                                type="string"
                                registration={register('insurance')}
                                errorMessage={errors.insurance?.message}
                            />
                        </div>
                        <div className="col-span-6">
                            <div className="space-y-4">
                                {address.map((add, index) => (
                                    <div key={index} className="flex items-center gap-3">
                                        <FormInputField
                                            // identifier={`direccion_${index}`}
                                            label={`Dirección ${index + 1}`}
                                            type="string"
                                            value={add.address}
                                            onChange={(e) => handleDiseaseChange(e, index)}
                                            // registration={register('address')}
                                        />

                                        {addressError[index] && <p className="text-red-700">{addressError[index]}</p>}

                                        {address.length > 1 && (
                                            <button
                                                type="button"
                                                onClick={() => removeDisease(index)}
                                                className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-red-100 hover:bg-red-200 text-red-600 text-xl font-bold transition"
                                                title="Eliminar"
                                            >
                                                &minus;
                                            </button>
                                        )}
                                    </div>
                                ))}

                                {address.length < 3 && (
                                    <div>
                                        <button
                                            type="button"
                                            onClick={addDisease}
                                            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded shadow transition"
                                        >
                                            <span className="text-lg leading-none">+</span>
                                            Añadir otra dirección
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                        {/* <div className="col-start-1 col-end-7 form-label-box">
              <FormInputField identifier="lat_long" label="Latitud y longitud" type="string" registration={register('lat_long')} errorMessage={errors.lat_long?.message} />
              <p className="text-right text-xs mt-2">Si necesita ayuda, lea este artículo sobre como sacar coordenadas de Google Maps.</p>
            </div> */}

                        <div className="col-start-1 col-end-12 form-label-box">
                            <FormInputField
                                identifier="latitude"
                                label="Latitud"
                                type="string"
                                registration={register('latitude')}
                                errorMessage={errors.latitude?.message}
                            />
                        </div>
                        <div className="col-start-1 col-end-12 form-label-box">
                            <FormInputField
                                identifier="longitude"
                                label="Longitud"
                                type="string"
                                registration={register('longitude')}
                                errorMessage={errors.longitude?.message}
                            />
                            <p className="text-right text-xs mt-2 font-medium	">
                                <a
                                    target="_blank"
                                    href="https://support.google.com/maps/answer/18539?hl=en&co=GENIE.Platform%3DDesktop#:~:text=Get%20the%20coordinates%20of%20a,decimal%20format%20at%20the%20top."
                                >
                                    Si necesita ayuda, lea este{' '}
                                    <span className="text-blue underline	">
                                        artículo sobre como sacar coordenadas de Google Maps.
                                    </span>
                                </a>
                            </p>
                        </div>
                        <div className="col-start-1 col-end-12 form-label-box">
                            <ControlledFormSelectDrop
                                label={'Ciudad'}
                                identifier={'idCity'}
                                options={allCities}
                                value={watchedCity}
                                registration={register('idCity')}
                                helperText={'Requerido'}
                                errorMessage={errors.idCity?.message}
                            />
                        </div>
                    </div>
                </CxCard>
            </CxWrap>
            {userInfo?.isSecondary !== true && (
                <div className=" pt-12 pr-12  text-right ">
                    <CxTabButton label="Actualizar" />
                </div>
            )}
            {showErrorModal && (
                <FailNotification
                    displayNotification={showErrorModal}
                    setCloseNotification={() => closeError()}
                    title={'Falla!'}
                    bodyText={errorMsg}
                />
            )}
            {showSuccessModal && (
                <CustomSuccessModal
                    title={'Acción ejecutada con éxito!'}
                    bodyText={successBody}
                    buttonText={'Cerrar'}
                    buttonAction={onCloseSuccessMessage}
                    open={showSuccessModal}
                    onClose={onCloseSuccessMessage}
                />
            )}
        </form>
    );
};

export default OwnProfile;
